import { Download, Edit, Plus, Send, SquareArrowUpRightIcon, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { emailHIInvoice, exportHIORPdf } from '@/services/homeInspectionService';
import { downloadFilefromBuffer } from '@/lib/utils';
import { toast } from '@/components/ui/use-toast';
import { displayError } from '@/lib/error';
import AlertBox from '@/components/AlertBox';
import { useClientById } from '@/services/clients';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { hasFeature } from '@/services/featureService';
import { useUserStore } from '@/stores/user';
import { Feature, HOME_INSPECTION_STATUS_OPTIONS } from '@/utils/constant';
import type { HIInvoiceResponse, HomeInspectionWorkOrderResponse } from '@/types/homeInspection.types';
import type { HiAttemptGetResponse } from './hiAttempt.schema';
import { HIShareAttemptUpdatesDialog } from './HIShareAttemptUpdatesDialog';
import { canShareHIAttemptUpdates } from './canShareHIAttemptUpdates';

const iconClasses = 'h-[20px] w-[20px] cursor-pointer';

const getClientInitials = (name?: string | null): string => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const statusClassMap: Record<string, string> = {
  CANCELED:        'bg-[#F5F5F5] text-[#676767]',
  ASSIGNED:        'bg-[#EDF5FF] font-medium text-primary',
  AWAITING_CLIENT: 'bg-[#FFEEBF] text-[#6C4A09]',
  CONFIRMED:       'bg-[#551175] text-[#FBEDFF]',
};

const collectRecipientEmails = (...sources: (string | string[] | null | undefined)[]): string[] =>
  Array.from(
    new Set(
      sources
        .flatMap((item) => (Array.isArray(item) ? item : item ? [item] : []))
        .map((email) => email.trim())
        .filter(Boolean)
    )
  );

type Props = {
  workOrder: HomeInspectionWorkOrderResponse;
  attempts: HiAttemptGetResponse[];
  invoice?: HIInvoiceResponse;
  onClose: () => void;
};

export const HIWorkOrderSidePanelHeader = ({ workOrder, attempts, invoice, onClose }: Props) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const loggedInUser = useUserStore((state) => state.user);
  const [isExporting, setIsExporting] = useState(false);
  const [isEmailingInvoice, setIsEmailingInvoice] = useState(false);
  const [invoiceConfirmationDialog, setInvoiceConfirmationDialog] = useState(false);
  const [displayRecipientEmails, setDisplayRecipientEmails] = useState(false);
  const [attemptShareDialogOpen, setAttemptShareDialogOpen] = useState(false);

  const status = workOrder.status;
  const statusLabel =
    HOME_INSPECTION_STATUS_OPTIONS.find((o) => o.value === status)?.label ?? status;

  const canShareAttemptUpdates = useMemo(
    () =>
      canShareHIAttemptUpdates(loggedInUser, attempts.length > 0, {
        status: workOrder.status,
        contactEmail: workOrder.contactEmail,
      }),
    [loggedInUser, attempts.length, workOrder.status, workOrder.contactEmail]
  );

  const canEmailInvoice = hasFeature(Feature.HOME_INSPECTION);
  const showSendMenu = canEmailInvoice || canShareAttemptUpdates;

  const { data: clientData } = useClientById(workOrder.clientId, { enabled: canEmailInvoice });
  const invoiceRecipientEmails = useMemo(
    () =>
      collectRecipientEmails(
        workOrder.contactEmail,
        clientData?.arInvoices,
        clientData?.affidavitWithInvoice,
        clientData?.arInvoicesCc,
        clientData?.affidavitWithInvoiceCc
      ),
    [
      workOrder.contactEmail,
      clientData?.arInvoices,
      clientData?.affidavitWithInvoice,
      clientData?.arInvoicesCc,
      clientData?.affidavitWithInvoiceCc,
    ]
  );

  const closeInvoiceConfirmationDialog = () => {
    setInvoiceConfirmationDialog(false);
    setDisplayRecipientEmails(false);
  };

  const handleEmailInvoice = async () => {
    if (!invoice?.id || isEmailingInvoice) return;
    setIsEmailingInvoice(true);
    try {
      const { data, error } = await emailHIInvoice(invoice.id);
      if (error) {
        displayError(error);
        return;
      }
      if (data?.status === 'FAILED') {
        toast({
          variant: 'destructive',
          description: data.message || 'Failed to email invoice.',
        });
        return;
      }
      toast({
        description: data?.message ?? 'Invoice emailed successfully.',
        duration: 3000,
      });
    } finally {
      setIsEmailingInvoice(false);
      closeInvoiceConfirmationDialog();
      // Both SENT and FAILED outcomes are logged server-side — refresh Billing tab email logs.
      await queryClient.invalidateQueries({ queryKey: ['hi-invoice', workOrder.workOrderId] });
    }
  };

  return (
    <div className="flex flex-col gap-4 p-6 pb-0 sticky top-0 bg-white">
      {/* Row 1: W/O number + action icons */}
      <div className="flex flex-row justify-between">
        <span className="font-semibold text-xl">{`W/O #: ${workOrder.workOrderNumber}`}</span>
        <div className="flex flex-row gap-2 h-[32px] [&_div]:border-[1px] [&_div]:p-[6px] [&_div]:rounded-md [&_div]:justify-center">
          {/* Send dropdown: Email Invoice + Email Attempt Updates */}
          {showSendMenu && (
            <DropdownMenu>
              <DropdownMenuTrigger>
                <div>
                  <Send color="#676767" className={iconClasses} />
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="ml-[-100px] min-w-[200px]">
                {canEmailInvoice && (
                  <DropdownMenuItem
                    disabled={!invoice?.id || isEmailingInvoice}
                    onSelect={() => setInvoiceConfirmationDialog(true)}
                  >
                    <div className="flex justify-between items-center gap-2 w-full text-[12px] text-primary font-[500]">
                      <span>{isEmailingInvoice ? 'Sending…' : 'Email Invoice'}</span>
                      <SquareArrowUpRightIcon className="h-4 w-4" color="#0A2240" />
                    </div>
                  </DropdownMenuItem>
                )}
                {canShareAttemptUpdates && (
                  <>
                    {canEmailInvoice && (
                      <DropdownMenuSeparator className="h-[2px]" />
                    )}
                    <DropdownMenuItem onSelect={() => setAttemptShareDialogOpen(true)}>
                      <div className="flex justify-between items-center gap-2 w-full text-[12px] text-primary font-[500]">
                        <span>Email Attempt Updates</span>
                        <SquareArrowUpRightIcon className="h-4 w-4" color="#0A2240" />
                      </div>
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Export HIOR PDF */}
          {hasFeature(Feature.HOME_INSPECTION_EXPORT_HIOR_PDF) && !!workOrder.server && (
            <div
              role="button"
              title="Export HIOR PDF"
              className={isExporting ? 'opacity-50 cursor-not-allowed pointer-events-none' : 'cursor-pointer'}
              onClick={async () => {
                if (isExporting) return;
                setIsExporting(true);
                try {
                  const { data, error } = await exportHIORPdf(workOrder.workOrderId);
                  if (error) {
                    toast({ description: 'FAILED TO EXPORT HIOR PDF', variant: 'destructive', duration: 5000 });
                  } else if (data) {
                    downloadFilefromBuffer(data, `${workOrder.workOrderNumber}.pdf`, 'PDF');
                  }
                } finally {
                  setIsExporting(false);
                }
              }}
            >
              <Download color="#676767" className={iconClasses} />
            </div>
          )}

          {/* Edit dropdown: Edit Work Order + Edit Attempt + Add Attempt */}
          {hasFeature(Feature.HOME_INSPECTION_WORK_ORDER_UPDATE) && (
            <DropdownMenu>
              <DropdownMenuTrigger>
                <div>
                  <Edit color="#676767" className={iconClasses} />
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="ml-[-100px] min-w-[180px]">
                <DropdownMenuItem disabled={status === 'CANCELED'}>
                  <div
                    className={`flex justify-between items-center gap-2 w-full text-[12px] font-[500] ${status === 'CANCELED' ? 'text-placeholder cursor-not-allowed' : 'text-primary'}`}
                    onClick={() => {
                      if (status !== 'CANCELED') navigate(`/home-inspection/${workOrder.workOrderId}/update`);
                    }}
                  >
                    <span>Edit Work Order</span>
                    <SquareArrowUpRightIcon className="h-4 w-4" color={status === 'CANCELED' ? '#969696' : '#0A2240'} />
                  </div>
                </DropdownMenuItem>
                <DropdownMenuSeparator className="h-[2px]" />
                <DropdownMenuItem
                  disabled={!attempts.length}
                >
                  <div
                    className={`flex justify-between items-center gap-2 w-full text-[12px] font-[500] ${!attempts.length ? 'text-placeholder cursor-not-allowed' : 'text-primary'}`}
                    onClick={() => {
                      const sorted = [...attempts].sort(
                        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
                      );
                      if (sorted[0]) {
                        navigate(`/home-inspection/${workOrder.workOrderNumber}/attempt/${sorted[0].attemptId}/edit`);
                      }
                    }}
                  >
                    <span>Edit Attempt</span>
                    <SquareArrowUpRightIcon className="h-4 w-4" color={!attempts.length ? '#969696' : '#0A2240'} />
                  </div>
                </DropdownMenuItem>
                <DropdownMenuSeparator className="h-[2px]" />
                <DropdownMenuItem>
                  <div
                    className="flex justify-between items-center gap-2 w-full text-[12px] text-primary font-[500]"
                    onClick={() => navigate(`/home-inspection/${workOrder.workOrderNumber}/attempt/create`)}
                  >
                    <span>Add Attempt</span>
                    <SquareArrowUpRightIcon className="h-4 w-4" color="#0A2240" />
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Add Attempt standalone button (when no existing attempts) */}
          {!hasFeature(Feature.HOME_INSPECTION_WORK_ORDER_UPDATE) && (
            <button
              type="button"
              title="Add Attempt"
              aria-label="Add Attempt"
              onClick={() => navigate(`/home-inspection/${workOrder.workOrderNumber}/attempt/create`)}
            >
              <Plus color="#676767" className={iconClasses} />
            </button>
          )}

          <div
            onClick={onClose}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClose(); } }}
            tabIndex={0}
            className="h-[32px] cursor-pointer"
            role="button"
            aria-label="Close panel"
          >
            <X color="#676767" className={iconClasses} />
          </div>
        </div>
      </div>

      {/* Row 2: Client avatar + name + client code */}
      <div className="flex flex-row justify-between align-middle">
        <div className="flex flex-row gap-2">
          <div className="bg-[#9747FF33] rounded-md text-normal font-semibold p-2 h-[48px] w-[48px] flex justify-center text-[#55288F] items-center">
            <span>{getClientInitials(workOrder.clientName)}</span>
          </div>
          <div className="flex flex-col break-words">
            <span className="text-base font-medium">{workOrder.clientName}</span>
            <span className="text-sm font-normal text-placeholder">
              {workOrder.clientCode ? `#${workOrder.clientCode}` : '-'}
            </span>
          </div>
        </div>

        {/* Status pill */}
        <div
          className={`h-[20px] px-3 text-xs font-medium rounded-full flex items-center justify-center ${statusClassMap[status] ?? ''}`}
        >
          {statusLabel}
        </div>
      </div>

      {/* Row 3: Work order address */}
      <div className="text-[#969696] font-[400] flex flex-col text-sm">
        <span className="break-words">
          {workOrder.propertyAddress}
        </span>
        <span>
          {[workOrder.city, workOrder.state, workOrder.zipCode].filter(Boolean).join(', ')}
        </span>
      </div>

      {invoiceConfirmationDialog && (
        <AlertBox
          title="Are you sure?"
          description={
            // spans only: DialogDescription renders a <p>, which cannot contain <div>
            <span>
              <span>Are you sure you want to send the invoice? </span>
              {displayRecipientEmails ? (
                <span className="flex flex-col gap-2">
                  <span>{invoiceRecipientEmails.join(', ')}</span>
                  <span onClick={() => setDisplayRecipientEmails(false)} className="text-primary cursor-pointer">
                    Hide Recipients
                  </span>
                </span>
              ) : (
                <span onClick={() => setDisplayRecipientEmails(true)} className="text-primary cursor-pointer">
                  View Recipients
                </span>
              )}
            </span>
          }
          isLoading={isEmailingInvoice}
          open={invoiceConfirmationDialog}
          cancelBtnLabel="No"
          confirmBtnLabel="Yes"
          cancelBtnClick={() => {
            if (!isEmailingInvoice) closeInvoiceConfirmationDialog();
          }}
          confirmBtnClick={() => {
            void handleEmailInvoice();
          }}
        />
      )}

      {attemptShareDialogOpen && attempts.length > 0 && (
        <HIShareAttemptUpdatesDialog
          onCloseClick={() => setAttemptShareDialogOpen(false)}
          attempts={attempts}
          workOrderId={workOrder.workOrderId}
          recipientEmail={workOrder.contactEmail}
          onEmailSent={() => {
            // Share email logs ride on the attempts list response — refresh only that query so the
            // Status Updates tab stays selected (invalidating the WO query remounts the panel tabs).
            void queryClient.invalidateQueries({ queryKey: ['hi-attempts', workOrder.workOrderId] });
          }}
        />
      )}
    </div>
  );
};
